import {
  AlertCaption,
  AlertColorType,
  Icon,
  LocaleDictionaryType,
  useLocales,
  useSwitchAlertIcon,
} from "@/lib/main";
import { CSSProperties, RefObject, useEffect, useRef, useState } from "react";
import Label from "./Label";
import Option from "./Option";
import Select from "./Select";

type OptionType<TValue extends SelectValue> = {
  id: string;
  optionLabel: LocaleDictionaryType;
  optionValue: TValue;
};

type SelectValue = string | number;

type CommonProps<TValue extends SelectValue> = {
  label: string;
  placeholder: string;
  options: OptionType<TValue>[];
  selectRef: RefObject<HTMLSpanElement | null>;
  disabled?: boolean;
  message?: string;
  alertColor?: AlertColorType;
  fullWidth?: boolean;
};

type SingleProps<TValue extends SelectValue> = CommonProps<TValue> & {
  mode?: "single";
  value: TValue | "";
  setValue: (value: TValue | "") => void;
};

type MultiProps<TValue extends SelectValue> = CommonProps<TValue> & {
  mode: "multi";
  values: TValue[];
  setValues: (values: TValue[]) => void;
  maxSelections?: number;
};

type Props<TValue extends SelectValue> =
  | SingleProps<TValue>
  | MultiProps<TValue>;

export default function SelectGroup<TValue extends SelectValue>(
  props: Props<TValue>,
) {
  const {
    label,
    placeholder,
    options,
    selectRef,
    disabled,
    message,
    mode,
    fullWidth,
  } = props;

  const l = useLocales();

  const alertColor = props.alertColor ?? "assist";

  const isMulti = mode === "multi";
  const singleValue = (props as SingleProps<TValue>).value;
  const selectedValues: TValue[] = isMulti
    ? (props as MultiProps<TValue>).values
    : singleValue === ""
      ? []
      : [singleValue as TValue];

  const maxReached = isMulti
    ? !!(props as MultiProps<TValue>).maxSelections &&
      selectedValues.length >= (props as MultiProps<TValue>).maxSelections!
    : false;

  const displayText =
    selectedValues.length === 0
      ? placeholder
      : isMulti && selectedValues.length > 1
        ? `${selectedValues.length}${
            (props as MultiProps<TValue>).maxSelections
              ? ` / ${(props as MultiProps<TValue>).maxSelections}`
              : ""
          } selected`
        : (options.find((o) => o.optionValue === selectedValues[0])
            ?.optionLabel ?? placeholder);

  const handleOptionClick = (optionValue: TValue) => {
    if (!isMulti) {
      const { value, setValue } = props as SingleProps<TValue>;
      setValue(value === optionValue ? "" : optionValue);
      setShowOptions(false);
    } else {
      const { values, setValues, maxSelections } = props as MultiProps<TValue>;
      if (values.includes(optionValue)) {
        setValues(values.filter((v) => v !== optionValue));
      } else if (!maxSelections || values.length < maxSelections) {
        setValues([...values, optionValue]);
      }
    }
  };

  const alertIcon = useSwitchAlertIcon(alertColor);
  const [showOptions, setShowOptions] = useState(false);
  const [maxOptionsHeight, setMaxOptionsHeight] = useState(216);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!showOptions) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setShowOptions(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showOptions]);

  const handleClick = () => {
    if (!showOptions && wrapperRef.current) {
      const wrapperBottom = wrapperRef.current.getBoundingClientRect().bottom;
      const footer = document.querySelector("footer");
      const footerTop = footer
        ? footer.getBoundingClientRect().top
        : window.innerHeight;
      setMaxOptionsHeight(Math.min(216, footerTop - wrapperBottom - 8));
    }
    setShowOptions((prev) => !prev);
  };

  const outerContainerStyles: CSSProperties = {
    position: "relative",
    marginBottom: "var(--spacing-sm)",
  };

  const optionsWrapperStyles: CSSProperties = {
    position: "absolute",
    top: "3rem",
    left: 0,
    right: 0,
    zIndex: 1000,
    display: "grid",
    gridTemplateRows: showOptions ? "1fr" : "0fr",
    transition: "grid-template-rows 0.5s ease",
    overflow: "hidden",
    borderRadius: "var(--border-radius-sm)",
  };

  const optionsInnerStyles: CSSProperties = {
    minHeight: 0,
    maxHeight: maxOptionsHeight,
    overflowY: "auto",
    backgroundColor: "var(--color-background-canvas)",
    border: showOptions
      ? "var(--border-width-sm) solid var(--color-border-quaternary)"
      : "none",
    borderRadius: "var(--border-radius-sm)",
  };

  return (
    <>
      <Label label={label} disabled={disabled} fullWidth={fullWidth}>
        <div ref={wrapperRef} style={outerContainerStyles}>
          <Select
            onClick={handleClick}
            value={selectedValues.length > 0 ? "selected" : ""}
            selectRef={selectRef}
            disabled={disabled}
            alertColor={alertColor}
          >
            {l(displayText)}
            <Icon
              name={showOptions ? "chevron-up" : "chevron-down"}
              color={disabled ? "disabled" : "secondary"}
            />
          </Select>

          <div style={optionsWrapperStyles}>
            <div style={optionsInnerStyles}>
              {options.map((option) => {
                const isSelected = selectedValues.includes(option.optionValue);
                return (
                  <Option
                    key={option.id}
                    isSelected={isSelected}
                    onSelect={() => handleOptionClick(option.optionValue)}
                    disabled={disabled || (maxReached && !isSelected)}
                    alertColor={alertColor}
                  >
                    {l(option.optionLabel)}
                  </Option>
                );
              })}
            </div>
          </div>
        </div>
      </Label>

      {message ? (
        <AlertCaption color={alertColor} icon={alertIcon}>
          {message}
        </AlertCaption>
      ) : null}
    </>
  );
}
